import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { useToast } from '../../toast';
import { ChannelToggles, type Channel } from '../../channels';
import { HANDOVER_TEMPLATES } from '../../handoverTemplates';
import { workspacePath } from '../../nav';
import { DirectAccessNote, useOrg } from '../Org';

/**
 * An org's settings, one card per decision.
 *
 * They used to run down the page as one long form, so the Save under the channels and the Disable under
 * the handover read as parts of the same thing. Each group is its own card now with its own button, and
 * the two that end access come last and are marked as such.
 */
export function OrgSettings() {
  const { tenantId, orgId, detail, orgDisabled, busy, act, setError } = useOrg();
  const navigate = useNavigate();
  const [label, setLabel] = useState(detail.org.label);
  const [channels, setChannels] = useState<Channel[]>(detail.org.channels);
  const [handoverTarget, setHandoverTarget] = useState(detail.org.handoverTarget ?? '');
  const [template, setTemplate] = useState(HANDOVER_TEMPLATES[0]?.id ?? '');
  const [saving, setSaving] = useState<null | 'channels' | 'handover'>(null);
  const toast = useToast();

  // The shell owns the loaded org, so anything that reloads it has to land here too.
  useEffect(() => {
    setLabel(detail.org.label);
    setChannels(detail.org.channels);
    setHandoverTarget(detail.org.handoverTarget ?? '');
  }, [detail.org.label, detail.org.channels, detail.org.handoverTarget]);

  function save<T>(which: 'channels' | 'handover', path: string, payload: object, after: (r: T) => void) {
    setSaving(which);
    setError(null);
    api<T>(`/api/tenants/${tenantId}/orgs/${orgId}/${path}`, { method: 'PATCH', body: payload })
      .then((r) => {
        after(r);
        toast.ok('Saved.');
      })
      .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Could not save'))
      .finally(() => setSaving(null));
  }

  return (
    <>
      <section className="card">
        <h2>Name</h2>
        <p className="lede">
          Taken from Salesforce when this org was connected, including which sandbox it is. Change it to
          whatever your team actually calls this customer — it is only a label here and means nothing to
          Salesforce.
        </p>
        <div className="row">
          <input
            value={label}
            maxLength={120}
            aria-label="Name"
            disabled={saving !== null}
            onChange={(e) => setLabel(e.target.value)}
          />
          {/* Goes through act rather than save, because the name is in the page heading and in the menu:
              the shell has to reload, and the menu refresh, or both keep showing what this org used to be
              called. */}
          <button
            type="button"
            disabled={busy || saving !== null || label.trim() === '' || label === detail.org.label}
            onClick={() =>
              void act(
                () => api(`/api/tenants/${tenantId}/orgs/${orgId}/label`, { method: 'PATCH', body: { label } }),
                'Renamed.',
              )
            }
          >
            {busy ? 'Saving…' : 'Rename'}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>What it may be used for</h2>
        <p className="lede">
          Only the ways of connecting you switch on here can be granted to anyone. Turning one off stops it
          immediately; it does not revoke the grants that used it, so turning it back on restores them.
        </p>
        <ChannelToggles
          value={channels}
          onChange={setChannels}
          available={detail.tenantChannels}
          disabled={saving !== null}
          ceilingName="your workspace"
        />
        <div className="actions" style={{ marginBottom: 16 }}>
          <button
            type="button"
            disabled={saving !== null || !channels.length}
            onClick={() =>
              save<{ channels: Channel[] }>('channels', 'channels', { channels }, (r) => setChannels(r.channels))
            }
          >
            {saving === 'channels' ? 'Saving…' : 'Save'}
          </button>
          {!channels.length && <span className="small muted">Choose at least one.</span>}
        </div>
        {channels.some((c) => c !== 'browser') && <DirectAccessNote myDomainUrl={detail.org.myDomainUrl} />}
      </section>

      {channels.some((c) => c === 'agent' || c === 'automation') && (
        <section className="card">
          <h2>Handing work over</h2>
          <p className="lede">
            If something in this org takes work and runs it in the background, describe it here. This text is
            the whole instruction an agent gets, and it follows it literally, so cover all four: how to ask
            what it can do, how to give it a task, which inputs carry the callback, and how to read the
            result afterwards. An agent given nothing to aim at describes its way around the org hunting for
            a target instead, which costs you API calls and fills the session log with reading nobody asked
            for.
          </p>
          {/* Nobody is going to write 500 characters of protocol from memory for the twentieth org this
              month. The wording for products we know about is ready; applying one is a copy into this
              field, so an org that has been edited since is never quietly rewritten. */}
          <div className="row" style={{ alignItems: 'flex-end', marginBottom: 8 }}>
            <label>
              Start from
              <select
                value={template}
                disabled={saving !== null}
                onChange={(e) => setTemplate(e.target.value)}
              >
                {HANDOVER_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="secondary"
              disabled={saving !== null}
              onClick={() => {
                const chosen = HANDOVER_TEMPLATES.find((t) => t.id === template);
                if (!chosen) return;
                // Only asked when there is something to lose, and it is their own wording at stake.
                if (
                  handoverTarget.trim() !== '' &&
                  !window.confirm('Replace the instruction currently in this box?')
                )
                  return;
                setHandoverTarget(chosen.text);
              }}
            >
              Use this wording
            </button>
          </div>
          {HANDOVER_TEMPLATES.find((t) => t.id === template) && (
            <p className="small muted" style={{ marginTop: 0 }}>
              {HANDOVER_TEMPLATES.find((t) => t.id === template)!.summary}
            </p>
          )}
          <label className="field">
            <span>How an agent asks what it can do, hands it a task, and reads the answer</span>
            <textarea
              rows={4}
              value={handoverTarget}
              maxLength={500}
              disabled={saving !== null}
              onChange={(e) => setHandoverTarget(e.target.value)}
              placeholder="Ask kruvi_ai__InvocableListTopics what Omni covers before deciding. To hand work over, call kruvi_ai__InvocableAI with the task as prompt, callbackUrl as notifyUrl, callbackToken as notifyKey and actingFor as actingFor. It returns conversationId. Once the handover reports finished, read the outcome with kruvi_ai__InvocableConversationStatus."
            />
          </label>
          <p className="small muted">
            Written in your own words and passed on exactly as given — Kruvi Access never reads it. Leave it empty
            if nothing here accepts work, and an agent will say so rather than go looking.
          </p>
          <div className="actions">
            <button
              type="button"
              disabled={saving !== null}
              onClick={() =>
                save<{ handoverTarget: string | null }>('handover', 'handover', { handoverTarget }, (r) =>
                  setHandoverTarget(r.handoverTarget ?? ''),
                )
              }
            >
              {saving === 'handover' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </section>
      )}

      {/* The two decisions that end access sit last and look different, so nobody scrolling for a Save
          button above lands on one of them by habit. */}
      <h2>Danger zone</h2>

      <section className="card danger-zone">
        <h3>Take this org out of service</h3>
        {orgDisabled ? (
          <>
            <p className="small muted">
              Nobody can open this org. Re-enabling restores its Salesforce users but not the grants — grant
              access again deliberately.
            </p>
            <button
              disabled={busy}
              onClick={() =>
                act(
                  () => api(`/api/tenants/${tenantId}/orgs/${orgId}/enable`, { method: 'POST' }),
                  'Org back in service. Nobody holds a grant on it until you give one.',
                )
              }
            >
              Put {detail.org.label} back in service
            </button>
          </>
        ) : (
          <>
            <p className="small muted">
              Disabling revokes every grant on this org and drops the held Connect token, so live sessions end
              within a minute. Nothing changes inside Salesforce, and the session log is kept — which is why an
              org is never deleted outright.
            </p>
            <button
              className="danger"
              disabled={busy}
              onClick={() => {
                if (
                  !window.confirm(
                    `Disable ${detail.org.label}? Every grant on it is revoked and live sessions end within a minute.`,
                  )
                )
                  return;
                void act(
                  () => api(`/api/tenants/${tenantId}/orgs/${orgId}/disable`, { method: 'POST' }),
                  'Org disabled and every grant on it revoked.',
                );
              }}
            >
              Disable {detail.org.label}
            </button>
          </>
        )}
      </section>

      <section className="card danger-zone">
        <h3>Remove it altogether</h3>
        <p className="small muted">
          For an org that is gone rather than idle — a sandbox that has been refreshed away, a customer you
          no longer work with. It disappears from every list here and cannot be brought back; connecting to
          the same org again registers it afresh.
        </p>
        <p className="small muted">
          Sessions already minted into it stay in the session log either way. That is the whole point of the
          log, and a sandbox being destroyed is not a reason for it to change.
        </p>
        <button
          className="danger"
          disabled={busy}
          onClick={() => {
            if (
              !window.confirm(
                `Remove ${detail.org.label}? It disappears from Kruvi Access and cannot be brought back. Any access to it ends within a minute.`,
              )
            )
              return;
            void act(async () => {
              await api(`/api/tenants/${tenantId}/orgs/${orgId}`, { method: 'DELETE' });
              // Nothing left to show, and the shell would reload into a 404. Back to the workspace's list
              // of orgs, where the menu lands too.
              navigate(workspacePath(tenantId), { replace: true });
            });
          }}
        >
          Remove {detail.org.label}
        </button>
      </section>
    </>
  );
}
